import type React from "react";
import { useMemo } from "react";

import { SWReactCountryIcons } from "@similarweb/icons";

import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import { i18nFilter } from "app/filters/i18nFilter";
import { swReactRankNumberFilter } from "app/filters/ngFilters";
import { WebsiteAnalysisTrendsBar } from "app/pages/website-analysis/components/WebsiteAnalysisTrendsBar";
import { WidgetLoader } from "app/pages/website-analysis/TrafficAndEngagement/Components/loaders";
import {
    BoxContainer,
    CountryIconContainer,
    CountryRowWrapper,
    NoDataWrapper,
    RankDataRow,
    RankNumberStyled,
    SmallSeparator,
    StyledLink,
    TextStyled,
} from "app/pages/website-analysis/TrafficAndEngagement/Components/StyledComponents";
import type { ITrafficAndEngagementBaseWidgetProps } from "app/pages/website-analysis/TrafficAndEngagement/types";
import { FlexRow } from "app/pages/website-analysis/website-content/leading-folders/components/Tab";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

export const WebsiteRankTable: React.FunctionComponent<ITrafficAndEngagementBaseWidgetProps> = ({
    data,
    isError,
    params,
    isLoading,
}) => {
    const dataObj = useMemo(() => {
        return data?.Data?.[params?.key];
    }, [data, params]);
    const countryMostVisitedWorldWide = dataObj?.Country;
    const isWorldWide = params.country === 999;
    const countryID = isWorldWide ? countryMostVisitedWorldWide : params.country;
    const countryName = useMemo(() => CountryService.getCountryById(countryID)?.text, [countryID]);
    const isUSState = useMemo(() => CountryService.isUSState(params.country), [params.country]);
    const splitCategory = useMemo(() => dataObj?.Category?.split("/") ?? [], [dataObj]);
    const isSubCategory = useMemo(() => splitCategory?.length > 1, [splitCategory]);
    const industryRedirectUrl = useMemo(() => {
        const urlParams = {
            country: countryID,
            duration: "1m",
            category: isSubCategory ? `${splitCategory?.[0]}~${splitCategory?.[1]}` : dataObj?.Category,
        };

        if (proNavigator.current().homeState === "salesIntelligence-home") {
            return null;
        }

        return proNavigator.getStateUrl("digitalsuite_marketresearch_webmarketanalysis_mapping", urlParams);
    }, [countryID, isSubCategory, splitCategory, dataObj]);

    const countryRedirectUrl = useMemo(() => {
        const urlParams = {
            country: countryID,
            duration: "1m",
            category: "All",
        };

        if (proNavigator.current().homeState === "salesIntelligence-home") {
            return null;
        }

        return proNavigator.getStateUrl("digitalsuite_marketresearch_webmarketanalysis_mapping", urlParams);
    }, [countryID]);

    const industryLabelText = isSubCategory ? `.../${splitCategory?.[1]}` : splitCategory?.[0];

    return isLoading ? (
        <WidgetLoader />
    ) : (
        <BoxContainer>
            {isError ? (
                <NoDataWrapper>
                    <NoDataLandscape
                        title="global.nodata.notavilable"
                        subtitle={null}
                    />
                </NoDataWrapper>
            ) : (
                <FlexColumn>
                    <RankDataRow>
                        <TextStyled>{i18nFilter()("wa.ao.ranks.global")}</TextStyled>
                        <FlexRow>
                            <RankNumberStyled>
                                {swReactRankNumberFilter()(dataObj?.GlobalRank.Value, 0)}
                            </RankNumberStyled>
                            {dataObj?.GlobalRank?.Trend?.length > 1 ? (
                                <WebsiteAnalysisTrendsBar
                                    data={dataObj?.GlobalRank.Trend}
                                    barsLimit={6}
                                    inverted
                                    rowName={"GlobalRank"}
                                />
                            ) : null}
                        </FlexRow>
                    </RankDataRow>
                    <SmallSeparator />
                    <RankDataRow>
                        <FlexColumn>
                            <TextStyled>{i18nFilter()("wa.ao.ranks.country")}</TextStyled>
                            <CountryRowWrapper>
                                <CountryIconContainer>
                                    <SWReactCountryIcons countryCode={isUSState ? 840 : countryMostVisitedWorldWide} />
                                </CountryIconContainer>
                                {countryRedirectUrl ? (
                                    <StyledLink
                                        label={countryName}
                                        url={countryRedirectUrl}
                                        target={isWorldWide ? "_blank" : null}
                                    />
                                ) : (
                                    <div>{countryName}</div>
                                )}
                            </CountryRowWrapper>
                        </FlexColumn>
                        <FlexRow>
                            <RankNumberStyled>
                                {swReactRankNumberFilter()(dataObj?.CountryRank?.Value, 0)}
                            </RankNumberStyled>
                            {dataObj?.CountryRank?.Trend?.length > 1 ? (
                                <WebsiteAnalysisTrendsBar
                                    data={dataObj?.CountryRank.Trend}
                                    barsLimit={6}
                                    inverted={true}
                                    rowName={"CountryRank"}
                                />
                            ) : null}
                        </FlexRow>
                    </RankDataRow>
                    <SmallSeparator />
                    <RankDataRow>
                        <FlexColumn>
                            <TextStyled>{i18nFilter()("wa.ao.ranks.category")}</TextStyled>
                            {industryRedirectUrl ? (
                                <StyledLink
                                    label={industryLabelText}
                                    url={industryRedirectUrl}
                                />
                            ) : (
                                <div>{industryLabelText}</div>
                            )}
                        </FlexColumn>
                        <FlexRow>
                            <RankNumberStyled>
                                {swReactRankNumberFilter()(dataObj?.CategoryRank.Value, 0)}
                            </RankNumberStyled>
                            {dataObj?.CategoryRank?.Trend?.length > 1 ? (
                                <WebsiteAnalysisTrendsBar
                                    data={dataObj?.CategoryRank.Trend}
                                    barsLimit={6}
                                    inverted={true}
                                    rowName={"CategoryRank"}
                                />
                            ) : null}
                        </FlexRow>
                    </RankDataRow>
                </FlexColumn>
            )}
        </BoxContainer>
    );
};
